import * as THREE from 'three'
import type {
  GSplatPickSample,
  GSplatSystemAssetSnapshot,
  SplatDataCpuPicker as SplatDataCpuPickerContract,
} from '@/plugins/gsplat/GSplatSystem'
import { EllipsoidRaycast } from '@/plugins/gsplat/spatial/EllipsoidRaycast'
import type { AssetId, PickQuery, RectPickQuery } from '@/shared/types/core'

const DEFAULT_CENTER_SPHERE_RADIUS = 0.02
const HIT_EPSILON = 1e-9

export interface SplatDataCpuPickSource {
  readonly assetId: AssetId
  readonly resourceGeneration: number
  /** 同一公共资产下的 Placement identity；LOD file child 继承父 Placement id。 */
  readonly placementId: string | number
  /** format-specific Resource identity；不同 LOD file 的 local splat index 允许相同。 */
  readonly sourceResourceId: string | number
  readonly node: THREE.Object3D
  readonly centers: Float32Array
  /** 读取 format-specific rotation 与实际半径；缺失时保持中心球兼容路径。 */
  readonly readSplatShape?: (
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ) => boolean
  /** 空数组表示从 0 到 centers.length / 3 的连续范围。 */
  readonly intervals?: readonly number[]
}

export interface SplatDataCpuPickerOptions {
  /** LOD WorldState 的当前 active file sources；静态 Resource 会由 asset snapshot 自动补齐。 */
  readonly readActiveSources?: (query: PickQuery) => readonly SplatDataCpuPickSource[]
}

interface CenterResourceIdentity {
  readonly centers?: Float32Array | null
  readonly readSplatShape?: SplatDataCpuPickSource['readSplatShape']
}

/** backend-neutral 的 center-sphere CPU picker；不创建或读取任何 GPU leaf。 */
export class SplatDataCpuPicker implements SplatDataCpuPickerContract {
  private readonly readActiveSources: (query: PickQuery) => readonly SplatDataCpuPickSource[]
  private disposed = false

  constructor(options: SplatDataCpuPickerOptions = {}) {
    this.readActiveSources = options.readActiveSources ?? (() => [])
  }

  pick(query: PickQuery, assets: readonly GSplatSystemAssetSnapshot[]): GSplatPickSample | null {
    this.assertActive()
    if (query.gpuOnly) return null

    const candidates = this.collectSources(query, assets)
    const assetOrder = new Map(assets.map((asset, index) => [asset.id, index]))
    const radius = normalizeRadius(query.centerSphereRadius)
    const ray = query.ray.clone()
    if (ray.direction.lengthSq() === 0) return null
    ray.direction.normalize()

    let best:
      | {
          readonly source: SplatDataCpuPickSource
          readonly sourceOrder: number
          readonly splatIndex: number
          readonly point: THREE.Vector3
          readonly rayParameter: number
          readonly normal?: THREE.Vector3
          readonly assetOrder: number
        }
      | undefined
    const center = new THREE.Vector3()
    const centerOffset = new THREE.Vector3()
    const shapePosition = new THREE.Vector3()
    const shapeRotation = new THREE.Quaternion()
    const shapeRadii = new THREE.Vector3()
    const seen = new Map<
      AssetId,
      Map<number, Map<string | number, Map<string | number, Set<number>>>>
    >()

    for (let sourceOrder = 0; sourceOrder < candidates.length; sourceOrder++) {
      const source = candidates[sourceOrder]
      const order = assetOrder.get(source.assetId)
      if (order === undefined || !this.matchesQuery(source, query)) continue
      source.node.updateMatrixWorld(true)
      const seenIndices = getSeenIndices(seen, source)
      forEachSourceIndex(source, (splatIndex) => {
        if (seenIndices.has(splatIndex)) return
        seenIndices.add(splatIndex)
        const offset = splatIndex * 3
        center
          .set(source.centers[offset], source.centers[offset + 1], source.centers[offset + 2])
          .applyMatrix4(source.node.matrixWorld)
        if (!isFiniteVector(center)) return
        const hasShape = source.readSplatShape?.(
          splatIndex,
          shapePosition,
          shapeRotation,
          shapeRadii
        )
        const intersection = hasShape
          ? EllipsoidRaycast.intersect(ray, {
              center: shapePosition,
              radii: shapeRadii,
              rotation: shapeRotation,
              matrixWorld: source.node.matrixWorld,
            })
          : null
        const rayParameter = intersection?.intersects
          ? intersection.distance
          : hasShape
            ? null
            : intersectSphere(ray, center, radius, centerOffset)
        if (rayParameter === null) return
        if (
          !best ||
          rayParameter < best.rayParameter - HIT_EPSILON ||
          (Math.abs(rayParameter - best.rayParameter) <= HIT_EPSILON &&
            (order < best.assetOrder ||
              (order === best.assetOrder &&
                (splatIndex < best.splatIndex ||
                  (splatIndex === best.splatIndex && sourceOrder < best.sourceOrder)))))
        ) {
          best = {
            source,
            sourceOrder,
            splatIndex,
            point: intersection?.intersects ? intersection.point : center.clone(),
            rayParameter,
            ...(intersection?.intersects ? { normal: intersection.normal.clone() } : {}),
            assetOrder: order,
          }
        }
      })
    }

    if (!best) return null
    return {
      assetId: best.source.assetId,
      resourceGeneration: best.source.resourceGeneration,
      point: best.point,
      distance: ray.origin.distanceTo(best.point),
      ...(best.normal ? { normal: best.normal } : {}),
      raw: Object.freeze({
        splatIndex: best.splatIndex,
        originalSplatIndex: best.splatIndex,
        placementId: best.source.placementId,
        sourceResourceId: best.source.sourceResourceId,
      }),
    }
  }

  pickRect(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly GSplatPickSample[] {
    this.assertActive()
    if (query.gpuOnly) return []
    if (query.kinds && !query.kinds.includes('gaussian-splat')) return []
    const centerNdc = new THREE.Vector2(
      (query.min.x + query.max.x) * 0.5,
      (query.min.y + query.max.y) * 0.5
    )
    const pointQuery: PickQuery = {
      ndc: centerNdc,
      ray: new THREE.Ray(),
      camera: query.camera,
      viewport: query.viewport ?? query.viewRect,
      viewId: query.viewId,
      viewRect: query.viewRect,
      layerMask: query.layerMask,
      kinds: query.kinds,
    }
    const candidates = this.collectSources(pointQuery, assets)
    const assetOrder = new Map(assets.map((asset, index) => [asset.id, index]))
    const minX = Math.min(query.min.x, query.max.x)
    const maxX = Math.max(query.min.x, query.max.x)
    const minY = Math.min(query.min.y, query.max.y)
    const maxY = Math.max(query.min.y, query.max.y)
    const seen = new Map<
      AssetId,
      Map<number, Map<string | number, Map<string | number, Set<number>>>>
    >()
    const results: Array<{
      readonly sample: GSplatPickSample
      readonly assetOrder: number
      readonly sourceOrder: number
      readonly splatIndex: number
    }> = []
    const center = new THREE.Vector3()
    for (let sourceOrder = 0; sourceOrder < candidates.length; sourceOrder++) {
      const source = candidates[sourceOrder]
      const order = assetOrder.get(source.assetId)
      if (order === undefined || !this.matchesQuery(source, pointQuery)) continue
      source.node.updateMatrixWorld(true)
      const seenIndices = getSeenIndices(seen, source)
      forEachSourceIndex(source, (splatIndex) => {
        if (seenIndices.has(splatIndex)) return
        seenIndices.add(splatIndex)
        const offset = splatIndex * 3
        center
          .set(source.centers[offset], source.centers[offset + 1], source.centers[offset + 2])
          .applyMatrix4(source.node.matrixWorld)
        if (!isFiniteVector(center)) return
        const projected = center.clone().project(query.camera)
        if (
          projected.x < minX ||
          projected.x > maxX ||
          projected.y < minY ||
          projected.y > maxY ||
          projected.z < -1 ||
          projected.z > 1
        ) {
          return
        }
        results.push({
          sample: {
            assetId: source.assetId,
            resourceGeneration: source.resourceGeneration,
            point: center.clone(),
            distance: query.camera.position.distanceTo(center),
            raw: Object.freeze({
              splatIndex,
              originalSplatIndex: splatIndex,
              placementId: source.placementId,
              sourceResourceId: source.sourceResourceId,
              source: 'cpu-rect',
            }),
          },
          assetOrder: order,
          sourceOrder,
          splatIndex,
        })
      })
    }
    results.sort(
      (a, b) =>
        (a.sample.distance ?? Number.POSITIVE_INFINITY) -
          (b.sample.distance ?? Number.POSITIVE_INFINITY) ||
        a.assetOrder - b.assetOrder ||
        a.splatIndex - b.splatIndex ||
        a.sourceOrder - b.sourceOrder
    )
    const maxResults =
      query.maxResults === undefined
        ? results.length
        : Number.isSafeInteger(query.maxResults) && query.maxResults >= 0
          ? query.maxResults
          : 0
    return results.slice(0, maxResults).map(({ sample }) => sample)
  }

  dispose(): void {
    this.disposed = true
  }

  private collectSources(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly SplatDataCpuPickSource[] {
    const result = [...this.readActiveSources(query)]
    const activeAssets = new Set(result.map(({ assetId }) => assetId))
    for (const asset of assets) {
      if (
        activeAssets.has(asset.id) ||
        !asset.mounted ||
        !asset.handle.visible ||
        !asset.handle.object3D
      ) {
        continue
      }
      const centers = (asset.resource.resourceIdentity as CenterResourceIdentity | undefined)
        ?.centers
      if (!centers) continue
      const identity = asset.resource.resourceIdentity as CenterResourceIdentity | undefined
      result.push({
        assetId: asset.id,
        resourceGeneration: asset.generation,
        placementId: asset.id,
        sourceResourceId: resolveStaticResourceId(asset),
        node: asset.handle.object3D,
        centers,
        readSplatShape: identity?.readSplatShape?.bind(identity) ?? asset.resource.readSplatShape,
      })
    }
    return result
  }

  private matchesQuery(source: SplatDataCpuPickSource, query: PickQuery): boolean {
    if (query.kinds && !query.kinds.includes('gaussian-splat')) return false
    if (query.assetIds && !query.assetIds.includes(source.assetId)) return false
    if (query.layerMask !== undefined && (source.node.layers.mask & query.layerMask) === 0) {
      return false
    }
    return source.node.visible
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('SplatDataCpuPicker 已销毁')
  }
}

function forEachSourceIndex(
  source: SplatDataCpuPickSource,
  callback: (splatIndex: number) => void
): void {
  const count = Math.floor(source.centers.length / 3)
  const intervals = source.intervals
  if (!intervals || intervals.length === 0) {
    for (let index = 0; index < count; index++) callback(index)
    return
  }
  if (intervals.length % 2 !== 0) throw new Error('GSplat CPU pick intervals 必须为 start/end 对')
  for (let interval = 0; interval < intervals.length; interval += 2) {
    const start = Math.max(0, intervals[interval])
    const end = Math.min(count, intervals[interval + 1])
    for (let index = start; index < end; index++) callback(index)
  }
}

function intersectSphere(
  ray: THREE.Ray,
  center: THREE.Vector3,
  radius: number,
  offset: THREE.Vector3
): number | null {
  offset.copy(center).sub(ray.origin)
  const projection = offset.dot(ray.direction)
  const perpendicularSquared = offset.lengthSq() - projection * projection
  const radiusSquared = radius * radius
  if (perpendicularSquared > radiusSquared) return null
  const halfChord = Math.sqrt(Math.max(0, radiusSquared - perpendicularSquared))
  const entry = projection - halfChord
  const exit = projection + halfChord
  if (exit < 0) return null
  return Math.max(0, entry)
}

function getSeenIndices(
  seen: Map<AssetId, Map<number, Map<string | number, Map<string | number, Set<number>>>>>,
  source: SplatDataCpuPickSource
): Set<number> {
  let generations = seen.get(source.assetId)
  if (!generations) {
    generations = new Map()
    seen.set(source.assetId, generations)
  }
  let placements = generations.get(source.resourceGeneration)
  if (!placements) {
    placements = new Map()
    generations.set(source.resourceGeneration, placements)
  }
  let resources = placements.get(source.placementId)
  if (!resources) {
    resources = new Map()
    placements.set(source.placementId, resources)
  }
  let indices = resources.get(source.sourceResourceId)
  if (!indices) {
    indices = new Set()
    resources.set(source.sourceResourceId, indices)
  }
  return indices
}

function resolveStaticResourceId(asset: GSplatSystemAssetSnapshot): string | number {
  const identity = asset.resource.resourceIdentity as { readonly id?: unknown } | undefined
  const id = identity?.id
  return typeof id === 'string' || typeof id === 'number' ? id : asset.id
}

function normalizeRadius(value: number | undefined): number {
  if (value === undefined) return DEFAULT_CENTER_SPHERE_RADIUS
  if (!Number.isFinite(value) || value < 0)
    throw new Error(`非法 GSplat centerSphereRadius: ${value}`)
  return value
}

function isFiniteVector(value: THREE.Vector3): boolean {
  return Number.isFinite(value.x) && Number.isFinite(value.y) && Number.isFinite(value.z)
}
